<template>
  <div id="appointment-recap">
    <div class="mt-5 p-5">
      <Button
        :label="$t('action.back')"
        icon="pi pi-arrow-left"
        class="p-button-secondary p-button-rounded p-button-outlined"
        @click="createAppointement"
      />
      <h2> {{ $t('appointmentrecap.title') }} </h2>
      <p>{{ $t('appointmentrecap.subtitle') }}</p>
      <div class="grid flex justify-content-center">
        <ul
          v-if="stakeholder"
          class="col-12 md:col-5"
        >
          <div class="flex">
            <em class="pi pi-user align-self-center" />
            <h3 class="ml-2">
              {{ $t('appointmentrecap.fieldset.stakeholder') }}
            </h3>
          </div>
          <li>{{ $t('entity.stakeholder.stakeholderId') }} : <strong>{{ stakeholder.stakeholderId }}</strong></li>
          <li>{{ $t('entity.stakeholder.name') }} : {{ stakeholder.name }}</li>
        </ul>
        <ul class="col-12 md:col-5 md:col-offset-1">
          <h3>{{ $t('appointmentrecap.fieldset.equipment') }}</h3>
          <li>{{ $t('appointmentrecap.quantity') }} : {{ equipmentQuantity }}</li>
        </ul>
        <div
          v-if="selectedSite"
          class="col-12 md:col-5"
        >
          <div class="flex">
            <em class="pi pi-map-marker align-self-center" />
            <h3 class="ml-2">
              {{ $t('appointmentrecap.fieldset.site') }}
            </h3>
          </div>
          <p>{{ selectedSite.name }}</p>
          <p>{{ selectedSite.address.name }}</p>
          <p>
            {{ selectedSite.address.postcode }} {{ selectedSite.address.city }}
          </p>
          <div class="flex">
            <i class="pi pi-phone align-self-center" />
            <p class="ml-3">
              {{ formatPhoneNumberAsInternational(selectedSite.phoneNumber, selectedSite.phoneNumberCountryCode) }}
            </p>
          </div>
        </div>
        <div
          v-if="appointments"
          class="col-12 md:col-5 md:col-offset-1"
        >
          <div class="flex">
            <em class="pi pi-calendar align-self-center" />
            <h3 class="ml-2">
              {{ $t('appointmentrecap.fieldset.appointment') }}
            </h3>
          </div>
          <ul
            v-for="appointment in appointments"
            :key="appointment.id"
          >
            <h4>{{ $t('entity.appointment.office') }} : <strong>{{ appointment.office.name }}</strong> </h4>
            <li class="toUpper">
              <strong>{{ getDate(appointment.start) }} - {{ $t('entity.planning.from') }} {{ getTime(appointment.start) }}
                {{ $t('entity.planning.to') }} {{ getTime(appointment.end) }} ({{
                  getDuration(appointment.start, appointment.end)
                }})</strong>
            </li>
          </ul>
        </div>
      </div>
      <div class="flex  flex-row justify-content-center align-items-baseline">
        <div class="mr-5 mb-5 pb-2 cursor ">
          <Checkbox
            v-model="conditions"
            :binary="true"
          />
          <u><a
            class="ml-3"
            @click="openConditionsDialog"
          >{{ $t('appointmentrecap.conditions') }}</a></u>
        </div>
        <div class="text-right mt-5">
          <Button
            class="p-button-rounded p-button-outlined"
            :label="$t('appointmentrecap.validate')"
            :disabled="!conditions"
            @click="validateAppointment"
          />
        </div>
      </div>
    </div>

    <Dialog
      v-model:visible="dialogConditions"
      class="col-10 table"
      :modal="true"
      :closable="false"
    >
      <div class="confirmation-content">
        <CguComponent />
      </div>
      <template #footer>
        <Button
          :label="$t('appointmentrecap.dialog.abortButton')"
          icon="pi pi-times"
          class="p-button-secondary mr-2 p-button-outlined p-button-rounded"
          @click="dialogConditions = false; conditions = false"
        />
        <Button
          :label="$t('appointmentrecap.dialog.confirmButton')"
          icon="pi pi-check"
          class="p-button-success mr-2 p-button-outlined p-button-rounded"
          @click="dialogConditions = false; conditions = true"
        />
      </template>
    </Dialog>
  </div>
</template>

<script>
import Button from 'primevue/button';
import Checkbox from 'primevue/checkbox'
import Dialog from 'primevue/dialog';
import CguComponent from "@/components/CguComponent.vue";

import {
  convertDateStringToDayLocaleString,
  convertDateStringToTimeLocaleString,
  convertMinsToHrsMinsString
} from "@/shared/utils/dateTimeUtils";
import moment from "moment";
import {formatPhoneNumberAsInternational} from "@/shared/utils/phoneNumberUtils";

export default {
  name: 'RequestRecap',
  components: { Button, Checkbox, Dialog, CguComponent },
  props: {
    stakeholder: {
      type: Object,
      required: true
    },
    equipmentQuantity: {
      type: Number,
      required: true
    },
    selectedSite: {
      type: Object,
      required: true
    },
    appointments: {
      type: Array,
      required: true
    },
    request: {
      type: Object,
      required: true
    }
  },
  emits: ['deselectSite', 'on-preview'],
  data() {
    return {
      userId: null,
      lastOffice: null,
      conditions: false,
      dialogConditions: false,
      timeout: null,
      days: ["entity.planning.monday", "entity.planning.tuesday", "entity.planning.wednesday", "entity.planning.thursday", "entity.planning.friday", "entity.planning.saturday", "entity.planning.sunday",]
    }
  },
  watch: {
    '$route'(to, from) {
      if (this.timeout) {
        console.info('Clear appointment redirection timeout')
        clearTimeout(this.timeout)
      }
    }
  },
  mounted() {
    this.userId = this.$route.params.userId
    this.timeout = setTimeout(this.validationTimeout, 10 * 60 * 1000)
  },
  methods: {
    formatPhoneNumberAsInternational,
    getDate(date) {
      return convertDateStringToDayLocaleString(date, this.$i18n.locale)
    },
    getTime(time) {
      return convertDateStringToTimeLocaleString(time, this.$i18n.locale)
    },
    getDuration(start, end) {
      let startDate = moment(start)
      let endDate = moment(end)
      let duration = moment.duration(endDate.diff(startDate)).asMinutes()
      return convertMinsToHrsMinsString(duration)
    },
    openConditionsDialog() {
      this.dialogConditions = true
    },
    async validationTimeout() {
      if (this.userId) {
        await this.$router.push({ name: 'userAppointment', params: { userId: this.userId } })
      } else {
        await this.$router.push({ name: 'userAppointment' })
      }
    },

    createAppointement() {
      this.deleteAppointment();
      this.$emit('on-preview')
    },

    async validateAppointment() {
      await this.$requestService.validateRequest(this.request.id)
      this.$emitter.emit('toast', { severity: 'success', message: 'appointmentPage.toast.validate' })
      if (this.userId) {
        await this.$router.push({ name: 'userAppointment', params: { userId: this.userId } })
      } else {
        await this.$router.push({ name: 'userAppointment' })
      }

    },

    async deleteAppointment(){
      await this.$requestService.deleteRequest(this.request.id)
      this.$emitter.emit('toast', { severity: 'success', message: 'appointmentPage.toast.deleted' })
    }
  }
}
</script>

<style lang="scss" scoped>
li {
  margin-left: 1rem;
  line-height: 2em;
}

h3 {
  color: $blue;
}

em {
  font-size: 2rem;
  color: $blue;
}

#appointment-recap {
  background: #f2f2f2;
}

[class*="col-"] {
  background: white;
  margin: 1rem 0 1rem 0;
  padding: 1rem;
}

.confirmation-content {
  text-align: justify;
  text-justify: auto;
}

h4 {
  margin: 0;
}


span .p-dialog-title {
  display: table !important;
  margin: 0 auto !important;
  background-color: red;
}

li.toUpper::first-letter {
  text-transform: capitalize;
}

@media (max-width: 768px) {
  #appointment-recap {
    .flex {
      flex-direction: column !important;
    }

    .text-right {
      margin-top: 0 !important;
    }

    .mr-5 {
      margin-right: 0 !important;
    }
  }
}
</style>
